/**
 * Order submission plumbing that sits between `usePlaceOrder`
 * (`hooks/use-trading-actions.ts`) and the `ExchangeClient`.
 *
 * Responsibilities:
 *   - `resolveAssetMeta`        — coin → `{ asset, szDecimals }` (cached, DEX-aware)
 *   - `formatOrderFields`       — price / size formatting per asset precision
 *   - `buildOrderType`          — map UI form state → Hyperliquid order-type wire shape
 *   - `ensureBuilderFeeApproved`— approve the builder fee once per address (localStorage-gated)
 *   - `ensureDexAbstraction`    — enable DEX abstraction for XYZ DEX on first use
 *
 * If you need to change order-wire shape or asset caching, this is the file.
 * If you need to change optimistic behavior, see `hooks/optimistic-updates.ts`.
 */
import type { InfoClient } from "@nktkas/hyperliquid";
import {
	getInfoClient,
	stripDexPrefix,
	XYZ_DEX_NAME,
} from "#/features/trade/lib/hyperliquid";
import {
	normalizeOrderSize,
	normalizePerpOrderPrice,
} from "#/features/trade/lib/order-format";
import {
	BUILDER_ADDRESS,
	BUILDER_ENABLED,
	BUILDER_FEE,
	BUILDER_FEE_RATE,
} from "#/hooks/builder-fee";

type MetaAndAssetCtxsResponse = Awaited<
	ReturnType<InfoClient["metaAndAssetCtxs"]>
>;
type PerpDexsResponse = Awaited<ReturnType<InfoClient["perpDexs"]>>;

export type AssetMeta = {
	asset: number;
	szDecimals: number;
};

const assetMetaCache = new Map<string, AssetMeta>();
let xyzDexLoaded = false;
const abstractionEnabledAddresses = new Set<string>();

export function createDisplayError(message: string, cause: unknown) {
	return new Error(message, {
		cause: cause instanceof Error ? cause : undefined,
	});
}

export function ensureDexAbstraction(
	exchangeClient: { agentEnableDexAbstraction: () => Promise<unknown> },
	address: string,
): Promise<void> {
	const key = `dex-abstraction:${address}`;
	if (abstractionEnabledAddresses.has(address) || localStorage.getItem(key))
		return Promise.resolve();
	return exchangeClient
		.agentEnableDexAbstraction()
		.then(() => {
			localStorage.setItem(key, "1");
			abstractionEnabledAddresses.add(address);
		})
		.catch(() => {
			localStorage.setItem(key, "1");
			abstractionEnabledAddresses.add(address);
		});
}

let builderFeeApprovalPromise: Promise<void> | null = null;

export function ensureBuilderFeeApproved(
	exchangeClient: {
		approveBuilderFee: (params: {
			maxFeeRate: string;
			builder: string;
		}) => Promise<unknown>;
	},
	address: string,
): Promise<void> {
	if (!BUILDER_ENABLED) return Promise.resolve();
	if (builderFeeApprovalPromise) return builderFeeApprovalPromise;

	builderFeeApprovalPromise = getInfoClient()
		.maxBuilderFee({
			user: address as `0x${string}`,
			builder: BUILDER_ADDRESS,
		})
		.then((maxFee) => {
			if (maxFee >= BUILDER_FEE) return;
			return exchangeClient
				.approveBuilderFee({
					maxFeeRate: BUILDER_FEE_RATE,
					builder: BUILDER_ADDRESS,
				})
				.then(() => {});
		})
		.finally(() => {
			builderFeeApprovalPromise = null;
		});

	return builderFeeApprovalPromise;
}

export function resolveAssetMeta(coin: string): Promise<AssetMeta> {
	const cached = assetMetaCache.get(coin);
	if (cached) return Promise.resolve(cached);

	const infoClient = getInfoClient();
	return (
		infoClient.metaAndAssetCtxs() as Promise<MetaAndAssetCtxsResponse>
	).then((response) => {
		const [meta] = response;
		for (const [i, item] of meta.universe.entries()) {
			assetMetaCache.set(item.name, { asset: i, szDecimals: item.szDecimals });
		}
		const assetMeta = assetMetaCache.get(coin);
		if (assetMeta) return assetMeta;

		if (xyzDexLoaded) throw new Error(`Unknown asset: ${coin}`);
		return loadXyzDexAssets(infoClient).then(() => {
			const xyzAssetMeta = assetMetaCache.get(coin);
			if (!xyzAssetMeta) throw new Error(`Unknown asset: ${coin}`);
			return xyzAssetMeta;
		});
	});
}

function loadXyzDexAssets(infoClient: InfoClient): Promise<void> {
	return Promise.all([
		infoClient.perpDexs() as Promise<PerpDexsResponse>,
		infoClient.metaAndAssetCtxs({
			dex: XYZ_DEX_NAME,
		}) as Promise<MetaAndAssetCtxsResponse>,
	]).then(([dexs, xyzResponse]) => {
		const perpDexIndex = dexs.findIndex(
			(d) => d !== null && d.name === XYZ_DEX_NAME,
		);
		if (perpDexIndex === -1) return;

		const offset = 100_000 + perpDexIndex * 10_000;
		const [meta] = xyzResponse;
		for (const [i, item] of meta.universe.entries()) {
			assetMetaCache.set(stripDexPrefix(item.name), {
				asset: offset + i,
				szDecimals: item.szDecimals,
			});
		}
		xyzDexLoaded = true;
	});
}

export type PlaceOrderParams = {
	coin: string;
	side: "buy" | "sell";
	orderType: string;
	price: string;
	size: string;
	reduceOnly: boolean;
	timeInForce: string;
	triggerPrice: string;
	markPrice: number;
	twapDuration?: string;
	twapNumSlices?: string;
};

export type CancelOrderParams = { coin: string; oid: number };
export type CancelTwapParams = { coin: string; twapId: number };

export function isTriggerOrderType(orderType: string) {
	return (
		orderType === "Stop limit" ||
		orderType === "Stop market" ||
		orderType === "Take profit limit" ||
		orderType === "Take profit market"
	);
}

export function buildOrderType(params: PlaceOrderParams) {
	const { orderType, timeInForce, triggerPrice } = params;
	switch (orderType) {
		case "Market order":
			return { limit: { tif: "FrontendMarket" as const } };
		case "Limit order":
			return {
				limit: {
					tif: (timeInForce === "IOC" ? "Ioc" : "Gtc") as "Gtc" | "Ioc",
				},
			};
		case "Stop limit":
			return {
				trigger: {
					isMarket: false,
					triggerPx: triggerPrice,
					tpsl: "sl" as const,
				},
			};
		case "Stop market":
			return {
				trigger: {
					isMarket: true,
					triggerPx: triggerPrice,
					tpsl: "sl" as const,
				},
			};
		case "Take profit limit":
			return {
				trigger: {
					isMarket: false,
					triggerPx: triggerPrice,
					tpsl: "tp" as const,
				},
			};
		case "Take profit market":
			return {
				trigger: {
					isMarket: true,
					triggerPx: triggerPrice,
					tpsl: "tp" as const,
				},
			};
		default:
			return { limit: { tif: "Gtc" as const } };
	}
}

export function formatOrderFields(
	params: PlaceOrderParams,
	szDecimals: number,
) {
	const formattedSize = normalizeOrderSize(params.size, szDecimals);
	const formattedPrice = normalizePerpOrderPrice(params.price, szDecimals);
	const formattedTriggerPrice = isTriggerOrderType(params.orderType)
		? params.triggerPrice === "0"
			? "0"
			: normalizePerpOrderPrice(params.triggerPrice, szDecimals)
		: "0";
	return { formattedSize, formattedPrice, formattedTriggerPrice };
}
